import { useMemo } from 'react';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { ASSET } from '@elementary/common-ui/constants/queryKeys';
import { getSemanticElementsQuery } from 'shared/services/api';

type SemanticColumnRaw = {
  id: string;
  name: string;
  displayName?: string;
  dataType?: string | null;
  description?: string | null;
};

type SemanticElementRaw = {
  id: string;
  name: string;
  description?: string | null;
  semanticType?: string;
  columns?: SemanticColumnRaw[];
};

export type SemanticColumnRow = {
  id: string;
  name: string;
  displayName?: string;
  elementType?: string;
  dataType?: string | null;
  description?: string | null;
};

const useSemanticElementsData = (parentId: string) => {
  const selectedEnvId = useSelectedEnv();

  const { data, isLoading } = useGraphqlQuery<{ items: SemanticElementRaw[] }>({
    query: getSemanticElementsQuery,
    queryKey: [selectedEnvId, ASSET, parentId, 'elements'],
    variables: { parentIds: [parentId] },
    enabled: !!parentId,
    select: (res: { assets: { items: SemanticElementRaw[] } }) => res.assets,
  });

  const rows: SemanticColumnRow[] = useMemo(() => {
    if (!data?.items) return [];
    return data.items.flatMap((element) =>
      (element.columns ?? []).map((col) => ({
        id: col.id,
        name: col.name,
        displayName: col.displayName,
        elementType: element.semanticType,
        dataType: col.dataType,
        description: col.description,
      })),
    );
  }, [data]);

  return { rows, isLoading };
};

export default useSemanticElementsData;
